import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react'
import { Navigate, createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { destinationFrom } from '@/identity/destination'
import { SignIn } from '@/identity/SignIn'
import { useSignOut } from '@/identity/SignOut'
import { useViewer } from '@/identity/viewer'

export const Route = createFileRoute('/signin')({
  component: SignInRoute,
  /**
   * Where the gate said somebody was headed, carried verbatim.
   *
   * Not validated here beyond its type: what counts as an address worth
   * honouring is a question about where this application will send people, and
   * it is answered in one place, by `destinationFrom`, rather than half here
   * and half there.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string | undefined } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
})

/**
 * The one page for everybody the console has nothing to show.
 *
 * Three answers, three things to say, and they are genuinely different: signed
 * out, signed in as somebody who may not read, and an API that cannot be
 * reached at all. Collapsing the middle one into the first is what leaves a
 * signed-in person staring at empty tables; collapsing the third into either is
 * what makes a broken deployment look like a product decision.
 *
 * An operator who lands here is sent on. That is not only the case of somebody
 * signing in — it is also the case of the gate having sent them here while the
 * API was unreachable, so the page recovers by itself once it can be read.
 */
function SignInRoute() {
  const { redirect } = Route.useSearch()
  const who = useViewer()

  // The two answers are opposites, and flashing the wrong one on every load
  // reads as the console losing track of who you are.
  if (!who.settled) return null

  if (who.failed) {
    return (
      <Centred>
        <Text color="fg.error">Couldn&rsquo;t reach the admin API. Check that it is running.</Text>
      </Centred>
    )
  }

  if (who.viewer === null) {
    return (
      <Centred>
        <SignIn />
      </Centred>
    )
  }

  if (!who.viewer.administrator) {
    return (
      <Centred>
        <NotAnOperator email={who.viewer.email} />
      </Centred>
    )
  }

  // `replace`: signing in is not a step anyone wants the back button to return
  // them to, and the gate that sent them here used `replace` for the same
  // reason.
  return <Navigate to={destinationFrom(redirect)} replace />
}

/**
 * Signed in, and still nothing to read — with the one thing that can be done
 * about it.
 *
 * The way out matters more here than anywhere else in the console. Signing in
 * as the wrong account leaves somebody in exactly this state, and every other
 * sign-out control is in the sidebar, which is behind the gate they have just
 * failed. Without this they would have no remedy from this surface at all.
 */
function NotAnOperator({ email }: { email: string }) {
  const { signOut, leaving, available } = useSignOut()

  return (
    <Stack gap="4" align="stretch" maxW="md">
      <Heading size="md">Not an operator</Heading>
      <Text color="fg.muted">
        You are signed in as {email}, which is not on this deployment&rsquo;s administrator list.
        Nothing here is readable to you.
      </Text>
      {available && (
        <Box>
          <Button variant="outline" size="sm" loading={leaving} onClick={signOut}>
            Sign in as someone else
          </Button>
        </Box>
      )}
    </Stack>
  )
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <Box minH="100dvh" bg="bg" display="grid" placeItems="center" px="6">
      {children}
    </Box>
  )
}
